import {
    Body_14_Coral_Empasis_14,
    Body_14_Coral_Primary_Regular_14,
    Body_16_Coral_Emphasis_16,
    Title_20_Coral_Strong_emphasis_20
} from "@UI/typography/generated/typography.tsx";
import {Gt, GtDatetime} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import {MenuLinkButton} from "@src/CommonComponents/MenuLinkButton/MenuLinkButton.tsx";
import {useCallback, useMemo} from "react";
import {FileEditIco} from "@UI/components/icons/FileChange.tsx";
import styles from "./requestsPage.module.less";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {Plus} from "@UI/components/icons/Plus.tsx";
import {FilterTile, FilterTiles} from "@src/CommonComponents/FilterTiles/FilterTiles.tsx";
import {useQuery} from "@tanstack/react-query";
import {apiClient} from "@src/httpClient/api.ts";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import cn from "classnames";
import {RequestStatusEnum, } from "@src/httpClient/__generated__/Api.ts";
import {ChevronLeft} from "@UI/components/icons/ChevronLeft.tsx";
import {ChevronRight} from "@UI/components/icons/ChevronRight.tsx";


const PAGE_LINK = "/requests/"
const PAGE_SIZE = 10

type RequestVariant = 'leave' | 'purchase' | 'overtime' | 'event'

const requestVariants: { value: RequestVariant, className: string, activeClassName: string, label: string }[] = [
    {value: 'leave', className: styles.leaveTile, activeClassName: styles.activeLeaveTile, label: 'Leave'},
    {value: 'purchase', className: styles.expenseTile, activeClassName: styles.activeExpenseTile, label: 'Expense'},
    {value: 'event', className: styles.eventTile, activeClassName: styles.activeEventTile, label: 'Event'},
]

export const RequestsPageMenuMutton = ({noPadding = true}: { noPadding?: boolean }) => {
    if (!noPadding) {
        return (
            <MenuLinkButton href={PAGE_LINK} icon={<FileEditIco size={22}/>}>
                <Body_16_Coral_Emphasis_16>
                    <Gt>Requests</Gt>
                </Body_16_Coral_Emphasis_16>
            </MenuLinkButton>
        )
    }
    return (
        <MenuLinkButton href={PAGE_LINK} icon={<FileEditIco size={22}/>} style={{padding: 0}}>
            <Body_16_Coral_Emphasis_16>
                <Gt>Requests</Gt>
            </Body_16_Coral_Emphasis_16>
        </MenuLinkButton>
    )
}

const statusColors: Record<string, string> = {
    [RequestStatusEnum.Pending]: Colors.Coral_mango_500,
    [RequestStatusEnum.Approved]: Colors.Coral_green_500,
    [RequestStatusEnum.Rejected]: Colors.Coral_special_Error_red,
    [RequestStatusEnum.Cancelled]: Colors.Obsidian_Light_400,
}

interface RequestRow {
    id: number
    status: string
    href: string
    employee_name?: string
    // leave
    vacation_type?: string
    date_from?: string
    date_to?: string
    // purchase
    category?: string
    expense_date?: string
    amount?: string
    // overtime
    date?: string
    overtime_hours?: string
    request_type?: string
    // event
    event_kind?: string
    recorded_at?: string
    reason?: string
}

const leaveHeaders = (isManager: boolean) => [
    ...(isManager ? [{key: 'employee', label: 'Employee'}] : []),
    {key: 'type', label: 'Type'},
    {key: 'from', label: 'From'},
    {key: 'to', label: 'To'},
    {key: 'status', label: 'Status'},
]

const purchaseHeaders = (isManager: boolean) => [
    ...(isManager ? [{key: 'employee', label: 'Employee'}] : []),
    {key: 'category', label: 'Category'},
    {key: 'date', label: 'Date'},
    {key: 'amount', label: 'Amount'},
    {key: 'status', label: 'Status'},
]

const overtimeHeaders = (isManager: boolean) => [
    ...(isManager ? [{key: 'employee', label: 'Employee'}] : []),
    {key: 'type', label: 'Type'},
    {key: 'date', label: 'Date'},
    {key: 'hours', label: 'Hours'},
    {key: 'status', label: 'Status'},
]

const eventHeaders = (isManager: boolean) => [
    ...(isManager ? [{key: 'employee', label: 'Employee'}] : []),
    {key: 'kind', label: 'Kind'},
    {key: 'date', label: 'Date'},
    {key: 'status', label: 'Status'},
]

const RequestsTable = ({tab}: { tab: RequestVariant }) => {
    const {t} = useTranslation()
    const employee = useCredentialsStore(x => x.employee)
    const isManager = !!employee?.is_manager
    const [params] = useSearchParams()
    const navigate = useNavigate()

    const page = useMemo(() => {
        const p = parseInt(params.get("page") ?? '1')
        return isNaN(p) ? 1 : p
    }, [params])

    const goToPage = useCallback((newPage: number) => {
        const p = new URLSearchParams(params)
        if (newPage <= 1) p.delete("page")
        else p.set("page", String(newPage))
        navigate(`?${p.toString()}`, {replace: true})
    }, [params])

    const headers = useMemo(() => {
        if (tab === 'purchase') return purchaseHeaders(isManager)
        if (tab === 'overtime') return overtimeHeaders(isManager)
        if (tab === 'event') return eventHeaders(isManager)
        return leaveHeaders(isManager)
    }, [tab, isManager])

    const {data, isLoading} = useQuery({
        queryKey: ['requests-list', tab, page, employee?.id],
        queryFn: async () => {
            const searchStr = new URLSearchParams()
            searchStr.set("page", String(page))
            searchStr.set("page_size", String(PAGE_SIZE))
            searchStr.set("ordering", "-date")

            if (tab === 'leave') {
                const r = await apiClient.api.workflowVacationRequestsList(undefined, {search: searchStr.toString()})
                const rows: RequestRow[] = (r.data.results ?? []).map(item => ({
                    id: item.id,
                    vacation_type: item.vacation_type ?? 'leave',
                    date_from: item.date_from,
                    date_to: item.date_to,
                    status: item.status ?? RequestStatusEnum.Pending,
                    employee_name: item.employee_name,
                    href: `/requests/leave/${item.id}`,
                }))
                return {rows, totalPages: r.data.total_pages ?? 1}
            }
            if (tab === 'purchase') {
                const r = await apiClient.api.workflowExpenseRequestsList(undefined, {search: searchStr.toString()})
                const rows: RequestRow[] = (r.data.results ?? []).map(item => ({
                    id: item.id,
                    category: item.category,
                    expense_date: item.expense_date,
                    amount: item.amount,
                    status: item.status ?? RequestStatusEnum.Pending,
                    employee_name: item.employee_name,
                    href: `/requests/purchase/${item.id}`,
                }))
                return {rows, totalPages: r.data.total_pages ?? 1}
            }
            if (tab === 'event') {
                const r = await apiClient.api.workflowEventRequestsList(undefined, {search: searchStr.toString()})
                const rows: RequestRow[] = (r.data.results ?? []).map(item => ({
                    id: item.id,
                    event_kind: item.event_kind,
                    recorded_at: item.recorded_at,
                    reason: item.reason,
                    status: item.status ?? RequestStatusEnum.Pending,
                    employee_name: item.employee_name,
                    href: `/requests/event/${item.id}`,
                }))
                return {rows, totalPages: r.data.total_pages ?? 1}
            }
            // overtime
            const r = await apiClient.api.workflowOvertimeRequestsList(undefined, {search: searchStr.toString()})
            const rows: RequestRow[] = (r.data.results ?? []).map(item => ({
                id: item.id,
                request_type: item.request_type,
                date: item.date,
                overtime_hours: item.overtime_hours,
                status: item.status ?? 'pending',
                employee_name: item.employee_name,
                href: `/requests/overtime/${item.id}`,
            }))
            return {rows, totalPages: r.data.total_pages ?? 1}
        },
        enabled: !!employee?.id,
    })

    const rows = data?.rows ?? []
    const totalPages = data?.totalPages ?? 1
    const colSpan = headers.length

    const renderRow = (row: RequestRow) => {
        if (tab === 'leave') return (
            <tr key={row.id} className={styles.tableRow} onClick={() => navigate(row.href)}>
                {isManager && <td>{row.employee_name}</td>}
                <td><Gt prefix={"LeaveTypeEnum"}>{row.vacation_type ?? ''}</Gt></td>
                <td><GtDatetime>{row.date_from ?? ''}</GtDatetime></td>
                <td><GtDatetime>{row.date_to ?? ''}</GtDatetime></td>
                <td>
                    <span className={styles.statusBadge}
                          style={{color: statusColors[row.status] ?? Colors.Obsidian_Light_400}}>
                        <Gt prefix={"RequestStatus"}>
                            {row.status}
                        </Gt>
                    </span>
                </td>
            </tr>
        )
        if (tab === 'event') return (
            <tr key={row.id} className={styles.tableRow} onClick={() => navigate(row.href)}>
                {isManager && <td>{row.employee_name}</td>}
                <td><Gt prefix={"EventKindEnum"}>{row.event_kind ?? ''}</Gt></td>
                <td><GtDatetime>{row.recorded_at ?? ''}</GtDatetime></td>
                <td>
                    <span className={styles.statusBadge}
                          style={{color: statusColors[row.status] ?? Colors.Obsidian_Light_400}}>
                        <Gt prefix={"RequestStatus"}>
                            {row.status}
                        </Gt>
                    </span>
                </td>
            </tr>
        )
        if (tab === 'purchase') return (
            <tr key={row.id} className={styles.tableRow} onClick={() => navigate(row.href)}>
                {isManager && <td>{row.employee_name}</td>}
                <td><Gt prefix={"ExpenseCategory"}>{row.category ?? ''}</Gt></td>
                <td><GtDatetime>{row.expense_date ?? ''}</GtDatetime></td>
                <td>{row.amount}</td>
                <td>
                    <span className={styles.statusBadge}
                          style={{color: statusColors[row.status] ?? Colors.Obsidian_Light_400}}>
                        <Gt prefix={"RequestStatus"}>
                            {row.status}
                        </Gt>
                    </span>
                </td>
            </tr>
        )
        return (
            <tr key={row.id} className={styles.tableRow} onClick={() => navigate(row.href)}>
                {isManager && <td>{row.employee_name}</td>}
                <td><Gt prefix={"RequestTypeEnum"}>{row.request_type ?? ''}</Gt></td>
                <td><GtDatetime>{row.date ?? ''}</GtDatetime></td>
                <td>{t('time_hours', {hours: row.overtime_hours})}</td>
                <td>
                    <span className={styles.statusBadge}
                          style={{color: statusColors[row.status] ?? Colors.Obsidian_Light_400}}>
                        <Gt prefix={"RequestStatus"}>
                            {row.status}
                        </Gt>
                    </span>
                </td>
            </tr>
        )
    }

    return (
        <Stack direction={"column"} gap={12}>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        {headers.map(h => <th key={h.key}><Gt>{h.label}</Gt></th>)}
                    </tr>
                    </thead>
                    <tbody>
                    {isLoading && (
                        <tr>
                            <td colSpan={colSpan} className={styles.tableEmpty}><Gt>Loading</Gt>...</td>
                        </tr>
                    )}
                    {!isLoading && rows.length === 0 && (
                        <tr>
                            <td colSpan={colSpan} className={styles.tableEmpty}><Gt>No data</Gt></td>
                        </tr>
                    )}
                    {rows.map(renderRow)}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <Stack justifyContent={"center"} alignItems={"center"} gap={16} className={styles.pagination}>
                    <Button
                        type={ButtonType.clickableIcon}
                        icon={<ChevronLeft/>}
                        disabled={page <= 1}
                        onClick={() => goToPage(page - 1)}
                    />
                    <Body_14_Coral_Primary_Regular_14>
                        {page} / {totalPages}
                    </Body_14_Coral_Primary_Regular_14>
                    <Button
                        type={ButtonType.clickableIcon}
                        icon={<ChevronRight/>}
                        disabled={page >= totalPages}
                        onClick={() => goToPage(page + 1)}
                    />
                </Stack>
            )}
        </Stack>
    )
}

export const RequestsPage = () => {
    const {t} = useTranslation()
    const [params] = useSearchParams()
    const navigate = useNavigate()

    const variant = useMemo(() => requestVariants.find(v => v.value === params.get("tab")), [params])

    return (
        <Stack direction={"column"} gap={16} className={cn(styles.subMenuPage, styles.listPage)}>
            <FilterTiles>
                {requestVariants.map(v => (
                    <FilterTile key={v.value}
                                className={v.className}
                                activeClassName={v.activeClassName}
                                active={v.value === variant?.value}
                                // Selecting resets the page; re-clicking the selected tile is a no-op
                                onClick={() => v.value !== variant?.value && navigate(`?tab=${v.value}`, {replace: true})}>
                        <Title_20_Coral_Strong_emphasis_20 style={{textAlign: "center"}}>
                            <Gt>{v.label}</Gt>
                        </Title_20_Coral_Strong_emphasis_20>
                    </FilterTile>
                ))}
            </FilterTiles>

            {variant
                ? <>
                    <RequestsTable tab={variant.value}/>
                    <Link to={`${PAGE_LINK}${variant.value}`} className={styles.addButton} aria-label={t('New request')}>
                        <Plus size={28} color={Colors.Obsidian_Light_0}/>
                    </Link>
                </>
                : <Body_14_Coral_Empasis_14>
                    <Gt>Please select the request type</Gt>
                </Body_14_Coral_Empasis_14>
            }
        </Stack>
    )
}
